<script>
import { mapActions, mapGetters } from 'vuex';
import RbxConfirmDialog from '@/shared/reybex/RbxConfirmDialog';
import downloader from '@/mixins/downloader';
import mainViewTabMixin from '@/mixins/mainViewTab';
import snackbarMixin from '@/mixins/snackbar';
import PrivacyAuditMainTab from './components/PrivacyAuditMainTab';
import PrivacyAuditForm from './components/PrivacyAuditForm';
import CategoryList from './components/CategoryList';
import MeasureList from './components/MeasureList';
import History from '../history';

export default {
  name: 'PrivacyAuditDetails',
  components: {
    PrivacyAuditMainTab,
    PrivacyAuditForm,
    CategoryList,
    MeasureList,
    History,
    RbxConfirmDialog,
  },
  mixins: [mainViewTabMixin, snackbarMixin, downloader],
  data() {
    return {
      query: {},
      params: {},
      privacyAudit: {},
      isLoading: false,
      isSaving: false,
      hasError: false,
      activeTab: 0,
      isConfirmDialogDisplayed: false,
      isDownloading: false,
      isInitialized: false,
    };
  },
  computed: {
    confirmDeletionMessage() {
      return `Soll das Audit ${this.privacyAudit.name} gelöscht werden?`;
    },
    ...mapGetters('app', ['auth']),
  },
  created() {
    const { query, params } = this.urlParser('/privacyAudit/show/:id?', this.path);

    this.query = query;
    this.params = params;
    this.fetchData();
  },
  methods: {
    ...mapActions('privacyAuditDetails', ['fetchPrivacyAudit', 'savePrivacyAudit', 'deletePrivacyAudit', 'setIsRefreshed']),
    ...mapActions('privacyAuditList', ['downloadReport']),
    fetchData() {
      this.isLoading = true;
      this.hasError = false;

      return this.fetchPrivacyAudit(this.params.id)
        .then(({ data }) => {
          this.privacyAudit = data;
          this.updateTabTitle({ path: this.path, title: this.privacyAudit.name });
        }).then(() => {
          this.isInitialized = true;
        })
        .catch(() => {
          this.hasError = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    save() {
      this.isSaving = true;

      return this.savePrivacyAudit(this.privacyAudit)
        .then(() => {
          this.showSuccessMessage('Das Audit wurde gespeichert.');
          this.setIsRefreshed(true);
        })
        .catch(() => {
          this.showErrorMessage('Die Aktualisierung ist fehlgeschlagen.');
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    delete() {
      return this.deletePrivacyAudit(this.privacyAudit)
        .then(() => {
          this.showSuccessMessage('Das Audit wurde gelöscht.');
        })
        .catch(() => {
          this.showErrorMessage('Das Löschen ist fehlgeschlagen.');
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    onRefuse() {
      this.isConfirmDialogDisplayed = false;
    },
    onConfirm() {
      this.delete(this.privacyAudit)
        .then(() => {
          this.isConfirmDialogDisplayed = false;
        });
    },
    finish() {
      this.privacyAudit.isFinished = true;
      this.save('Das Audit ist nun abgeschlossen. Bitte tragen Sie das Fazit ein.');
    },
    unlock() {
      this.privacyAudit.isFinished = false;
      this.save('Das Audit wurde entsperrt.');
    },
    _downloadReport() {
      this.isDownloading = true;
      const self = this;
      this.downloadReport(this.privacyAudit.id)
        .then((response) => {
          this.getDownloadContent(response, 'audit_report.pdf');
        })
        .catch(() => {
          this.showErrorMessage('Beim Erstellen des Berichts ist ein Fehler aufgetreten.');
        })
        .finally(() => {
          self.isDownloading = false;
        });
    },
  },
};
</script>

<template>
  <div class="privacyAudit-detail">
    <v-progress-linear v-if="isLoading" :indeterminate="true"/>

    <span
      v-else-if="hasError"
      class="js-error ma-3"
    >An error occurred while fetching privacyAudit information</span>

    <template v-else>

      <rbx-confirm-dialog @confirm="onConfirm" @refuse="onRefuse"
                          :message="confirmDeletionMessage"
                          :is-displayed="isConfirmDialogDisplayed" />

      <v-toolbar dark dense height="40" color="blue-grey lighten-2">

        <v-tooltip bottom>
          <v-btn @click="fetchData" icon slot="activator">
            <v-icon>refresh</v-icon>
          </v-btn>
          <span>Aktualisieren</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn @click="save" :loading="isSaving" icon slot="activator">
            <v-icon>save</v-icon>
          </v-btn>
          <span>Speichern</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn v-if="auth.hasDeleteRights"
                 @click="isConfirmDialogDisplayed = true" icon slot="activator">
            <v-icon>delete</v-icon>
          </v-btn>
          <span>Löschen</span>
        </v-tooltip>

        <v-tooltip bottom v-if="!privacyAudit.isFinished">
          <v-btn @click="finish" icon slot="activator">
            <v-icon>lock</v-icon>
          </v-btn>
          <span>Audit abschließen</span>
        </v-tooltip>
        <v-tooltip bottom v-else>
          <v-btn @click="unlock" icon slot="activator">
            <v-icon>lock_open</v-icon>
          </v-btn>
          <span>Audit entsperren</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn
            v-if="auth.hasDownloadRights"
            @click.native="_downloadReport"
            flat icon slot="activator"
            :disabled="isDownloading"
          >
            <v-icon small>cloud_download</v-icon>
          </v-btn>
          <span>Bericht herunterladen</span>
        </v-tooltip>
      </v-toolbar>

      <v-tabs v-model="activeTab"
              height="35"
              slider-color="black"
              color="amber">

        <v-tab ripple>Audit-Daten</v-tab>
        <v-tab ripple>Fragen</v-tab>
        <v-tab ripple>Maßnahmen</v-tab>
        <v-tab ripple>Historie</v-tab>

        <v-tab-item>
          <PrivacyAuditForm :privacy-audit="privacyAudit" @save="save" />
        </v-tab-item>
        <v-tab-item>
          <CategoryList
            :categories="privacyAudit.categories"
            :privacy-audit-id="privacyAudit ? privacyAudit.id : null"
            :disabled="privacyAudit.isFinished" />
        </v-tab-item>
        <v-tab-item>
          <MeasureList
            v-if="isInitialized"
            :privacy-audit-id="privacyAudit ? privacyAudit.id : null"
            :disabled="privacyAudit.isFinished" />
        </v-tab-item>
        <v-tab-item>
          <History :domain="'privacyAudit'" :domain-id="privacyAudit.id" path="/history" />
        </v-tab-item>
      </v-tabs>
    </template>
  </div>
</template>

<style lang="scss">
.privacyAudit-detail {
  &-title {
    margin-bottom: 10px;
  }

  &-actions {
    margin: 5px 0;

    .v-btn:first-child {
      border-radius: 5px 0 0 5px;
    }

    .v-btn:last-child {
      border-radius: 0 5px 5px 0;
    }

    .v-btn + .v-btn {
      border-left: 1px solid #fff !important;
    }

    .v-btn {
      margin: 0;
      width: 35px;
      height: 35px;
      border-radius: 0px;
    }
  }

  &-tab {
    padding-top: 10px;

    &-title {
      margin-bottom: 10px;
    }
  }

  &-common-list {
    margin-top: 5px;
    .v-btn {
      margin: 0px;
      width: 30px;
      height: 30px;
    }

    &-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
    }

    &-item {
      display: flex;
      align-items: center;
      justify-content: space-between;

      &:hover {
        .v-btn {
          opacity: 1;
        }
      }

      .v-btn {
        opacity: 0;
      }
    }
  }

  .v-tabs {
    &__items {
      padding-bottom: 30px;
    }
  }

  .submit-button {
    margin-top: 20px;
  }
}
</style>
